// ============================================================================
// MarketplacePage.jsx — Zenna Design Marketplace
// Browse, search, filter, buy designs, templates, fonts
// ============================================================================

import { useState, useEffect, useCallback } from 'react'
import ShareButtons from './ShareButtons'
import ZennaPageHeader from './ZennaPageHeader'
import CreateListingModal from './CreateListingModal'

const API_URL = import.meta.env.VITE_API_URL || ''

// ============================================================================
// LISTING CARD
// ============================================================================

function ListingCard({ listing, onView }) {
  return (
    <div
      onClick={() => onView && onView(listing)}
      className="bg-slate-800/80 rounded-2xl overflow-hidden border border-slate-700/50 hover:border-zenna-violet/30 transition-all cursor-pointer group hover:shadow-lg hover:shadow-zenna-violet/5"
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden">
        <img
          src={listing.thumbnail_url || listing.preview_images?.[0]}
          alt={listing.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />

        {/* Price badge */}
        <div className="absolute top-2 right-2">
          {listing.price_personal <= 0 ? (
            <span className="bg-green-500/90 text-white text-[11px] font-bold px-2.5 py-1 rounded-full">FREE</span>
          ) : (
            <span className="bg-slate-900/80 backdrop-blur-sm text-white text-[11px] font-bold px-2.5 py-1 rounded-full">
              ${listing.price_personal.toFixed(2)}
            </span>
          )}
        </div>

        {/* Featured badge */}
        {listing.is_featured && (
          <div className="absolute top-2 left-2 bg-amber-500/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            ⭐ Featured
          </div>
        )}

        {/* Category badge */}
        <div className="absolute bottom-2 left-2 flex gap-1">
          <span className="bg-slate-900/70 backdrop-blur-sm text-slate-300 text-[10px] px-2 py-0.5 rounded-full capitalize">
            {listing.category}
          </span>
          {listing.item_type === 'physical' && (
            <span className="bg-amber-500/80 text-white text-[10px] px-2 py-0.5 rounded-full">📦 Ships</span>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <h3 className="text-sm font-medium text-white line-clamp-1 mb-1">{listing.title}</h3>

        {/* Seller */}
        <div className="flex items-center gap-1.5 mb-2">
          {listing.seller?.avatar_url ? (
            <img src={listing.seller.avatar_url} alt="" className="w-4 h-4 rounded-full" />
          ) : (
            <div className="w-4 h-4 rounded-full bg-gradient-to-br from-zenna-violet to-zenna-cyan flex items-center justify-center text-[8px] text-white font-bold">
              {(listing.seller?.display_name || '?')[0]}
            </div>
          )}
          <span className="text-[11px] text-slate-500 truncate">{listing.seller?.display_name || 'Anonymous'}</span>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3 text-[10px] text-slate-500">
          {listing.rating_count > 0 && (
            <span className="flex items-center gap-0.5">
              ⭐ {listing.rating_avg?.toFixed(1)} ({listing.rating_count})
            </span>
          )}
          <span>{listing.purchase_count || 0} sold</span>
          {listing.craft_type && (
            <span className="text-zenna-cyan/60 capitalize">{listing.craft_type.replace(/-/g, ' ')}</span>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// LISTING DETAIL MODAL
// ============================================================================

function ListingDetailModal({ listing, userEmail, onClose, onPurchased }) {
  const [selectedLicense, setSelectedLicense] = useState('personal')
  const [purchasing, setPurchasing] = useState(false)
  const [currentImage, setCurrentImage] = useState(0)
  const [shippingZip, setShippingZip] = useState('')
  const [shippingRates, setShippingRates] = useState([])
  const [selectedShipping, setSelectedShipping] = useState(null)
  const [loadingRates, setLoadingRates] = useState(false)
  const [quantity, setQuantity] = useState(1)

  if (!listing) return null

  const images = listing.preview_urls?.length > 0 ? listing.preview_urls 
    : listing.preview_images?.length > 0 ? listing.preview_images 
    : [listing.thumbnail_url]
  const isPhysical = listing.item_type === 'physical'

  const prices = {
    personal: listing.price_personal || 0,
    commercial: listing.price_commercial || null,
    extended: listing.price_extended || null
  }

  // Fetch shipping rates
  const getShippingRates = async () => {
    if (!shippingZip || shippingZip.length < 5) return
    setLoadingRates(true)
    try {
      const res = await fetch(`${API_URL}/orders/shipping-rates?listing_id=${listing.id}&to_zip=${shippingZip}`)
      const data = await res.json()
      setShippingRates(data.rates || [])
      if (data.rates?.length > 0) setSelectedShipping(data.rates[0])
    } catch (e) { console.error(e) }
    setLoadingRates(false)
  }

  const itemTotal = (prices[selectedLicense] || 0) * quantity
  const shippingCost = isPhysical ? (selectedShipping?.rate || 0) : 0
  const orderTotal = itemTotal + shippingCost

  const handlePurchase = async () => {
    if (!userEmail) return
    if (isPhysical && listing.shipping_type === 'calculated' && !selectedShipping) return
    setPurchasing(true)
    try {
      const res = await fetch(`${API_URL}/orders/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail },
        body: JSON.stringify({
          listing_id: listing.id,
          license_type: selectedLicense,
          quantity,
          shipping_method: isPhysical ? (selectedShipping?.service || listing.shipping_type) : null,
          shipping_address: isPhysical && shippingZip ? { zip: shippingZip } : null
        })
      })
      const data = await res.json()

      if (data.checkout_url) {
        window.location.href = data.checkout_url
      } else if (data.download_url) {
        if (onPurchased) onPurchased(listing.id)
        const link = document.createElement('a')
        link.href = data.download_url
        link.download = listing.title
        link.click()
      }
    } catch (err) {
      console.error('Purchase error:', err)
    }
    setPurchasing(false)
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-700" onClick={e => e.stopPropagation()}>
        <div className="flex flex-col md:flex-row">
          {/* Images */}
          <div className="flex-1 p-4">
            <div className="relative rounded-xl overflow-hidden bg-black aspect-square mb-3">
              <img src={images[currentImage]} alt={listing.title} className="w-full h-full object-contain" />
              <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 bg-black/50 rounded-full text-white flex items-center justify-center hover:bg-black/70">✕</button>
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setCurrentImage(i)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${
                      i === currentImage ? 'border-zenna-violet' : 'border-slate-700 opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="w-full md:w-96 p-5 border-t md:border-t-0 md:border-l border-slate-700">
            <h2 className="text-xl font-bold text-white mb-1">{listing.title}</h2>

            {/* Seller */}
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-zenna-violet to-zenna-cyan flex items-center justify-center text-[10px] text-white font-bold">
                {(listing.seller?.display_name || '?')[0]}
              </div>
              <span className="text-sm text-slate-400">{listing.seller?.display_name}</span>
              {listing.seller?.badge === 'founding' && (
                <span className="text-[9px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full">⭐ Founder</span>
              )}
            </div>

            {/* Rating + stats */}
            <div className="flex items-center gap-3 text-xs text-slate-500 mb-4">
              {listing.rating_count > 0 && <span>⭐ {listing.rating_avg?.toFixed(1)} ({listing.rating_count} reviews)</span>}
              <span>{listing.purchase_count} sold</span>
              <span>{listing.view_count} views</span>
            </div>

            {/* Description */}
            {listing.description && (
              <p className="text-sm text-slate-400 mb-4 whitespace-pre-line">{listing.description}</p>
            )}

            {/* Tags */}
            {listing.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-4">
                {listing.tags.map(tag => (
                  <span key={tag} className="text-[10px] text-zenna-cyan/80 bg-zenna-cyan/10 px-2 py-0.5 rounded-full">#{tag}</span>
                ))}
              </div>
            )}

            {/* File info / Item details */}
            <div className="bg-slate-700/30 rounded-lg p-3 mb-4 space-y-1">
              <div className="flex justify-between text-xs"><span className="text-slate-500">Type</span><span className="text-slate-300">{isPhysical ? '📦 Physical Item' : '📥 Digital Download'}</span></div>
              {listing.file_type && <div className="flex justify-between text-xs"><span className="text-slate-500">Format</span><span className="text-slate-300 uppercase">{listing.file_type}</span></div>}
              {listing.craft_type && <div className="flex justify-between text-xs"><span className="text-slate-500">Craft</span><span className="text-slate-300 capitalize">{listing.craft_type.replace(/[-_]/g, ' ')}</span></div>}
              {listing.colors_used > 0 && <div className="flex justify-between text-xs"><span className="text-slate-500">Colors</span><span className="text-slate-300">{listing.colors_used}</span></div>}
              {listing.stitch_count > 0 && <div className="flex justify-between text-xs"><span className="text-slate-500">Stitches</span><span className="text-slate-300">{listing.stitch_count.toLocaleString()}</span></div>}
              {isPhysical && listing.weight_oz && <div className="flex justify-between text-xs"><span className="text-slate-500">Weight</span><span className="text-slate-300">{listing.weight_oz >= 16 ? `${(listing.weight_oz/16).toFixed(1)} lbs` : `${listing.weight_oz} oz`}</span></div>}
              {isPhysical && listing.handling_time_days && <div className="flex justify-between text-xs"><span className="text-slate-500">Ships in</span><span className="text-slate-300">{listing.handling_time_days} business day{listing.handling_time_days > 1 ? 's' : ''}</span></div>}
              {isPhysical && listing.inventory_count != null && !listing.inventory_unlimited && <div className="flex justify-between text-xs"><span className="text-slate-500">In stock</span><span className={listing.inventory_count < 3 ? 'text-amber-400' : 'text-slate-300'}>{listing.inventory_count} available</span></div>}
              {isPhysical && listing.shipping_type === 'free' && <div className="flex justify-between text-xs"><span className="text-slate-500">Shipping</span><span className="text-zenna-emerald font-medium">✅ Free shipping</span></div>}
            </div>

            {/* Quantity for physical items */}
            {isPhysical && (
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs text-slate-400">Qty:</span>
                <div className="flex items-center border border-slate-600 rounded-lg overflow-hidden">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-3 py-1 text-slate-400 hover:bg-slate-700">−</button>
                  <span className="px-3 py-1 text-white text-sm bg-slate-700/50 min-w-[40px] text-center">{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} className="px-3 py-1 text-slate-400 hover:bg-slate-700">+</button>
                </div>
              </div>
            )}

            {/* Shipping calculator (physical + calculated only) */}
            {isPhysical && listing.shipping_type === 'calculated' && (
              <div className="mb-4 bg-slate-700/30 rounded-lg p-3">
                <div className="text-xs text-slate-400 font-semibold uppercase mb-2">📦 Shipping Calculator</div>
                <div className="flex gap-2">
                  <input 
                    type="text" maxLength={5} value={shippingZip}
                    onChange={e => setShippingZip(e.target.value.replace(/\D/g, ''))}
                    placeholder="ZIP code"
                    className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500"
                  />
                  <button onClick={getShippingRates} disabled={shippingZip.length < 5 || loadingRates}
                    className="px-4 py-2 bg-zenna-violet/20 text-zenna-violet rounded-lg text-xs font-medium hover:bg-zenna-violet/30 disabled:opacity-40">
                    {loadingRates ? '...' : 'Get Rates'}
                  </button>
                </div>
                {shippingRates.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {shippingRates.map(rate => (
                      <button key={rate.service} onClick={() => setSelectedShipping(rate)}
                        className={`w-full flex justify-between items-center p-2 rounded-lg text-xs transition-all ${
                          selectedShipping?.service === rate.service 
                            ? 'bg-zenna-violet/10 border border-zenna-violet/30' 
                            : 'hover:bg-slate-600/30 border border-transparent'
                        }`}>
                        <div>
                          <span className="text-white font-medium">{rate.name}</span>
                          <span className="text-slate-500 ml-2">{rate.estimated_days} days</span>
                        </div>
                        <span className="text-white font-medium">${rate.rate.toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* License selection (digital) */}
            {!isPhysical && (
              <div className="space-y-2 mb-4">
                <div className="text-xs text-slate-400 font-semibold uppercase">License</div>
                {[
                  { key: 'personal', label: 'Personal Use', desc: 'For personal crafting only' },
                  { key: 'commercial', label: 'Small Business', desc: 'Sell up to 100 items' },
                  { key: 'extended', label: 'Extended/Commercial', desc: 'Unlimited commercial use' }
                ].filter(l => prices[l.key] !== null).map(license => (
                  <button
                    key={license.key}
                    onClick={() => setSelectedLicense(license.key)}
                    className={`w-full flex items-center justify-between p-3 rounded-xl text-left transition-all ${
                      selectedLicense === license.key
                        ? 'bg-zenna-violet/10 border border-zenna-violet/40'
                        : 'bg-slate-700/30 border border-transparent hover:border-slate-600'
                    }`}
                  >
                    <div>
                      <div className="text-sm text-white font-medium">{license.label}</div>
                      <div className="text-[10px] text-slate-500">{license.desc}</div>
                    </div>
                    <div className="text-sm font-bold text-white">
                      {prices[license.key] <= 0 ? 'FREE' : `$${prices[license.key].toFixed(2)}`}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Order total */}
            {(isPhysical && shippingCost > 0) && (
              <div className="bg-slate-700/30 rounded-lg p-3 mb-4 space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-slate-500">Item{quantity > 1 ? ` × ${quantity}` : ''}</span><span className="text-white">${itemTotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span className="text-white">${shippingCost.toFixed(2)}</span></div>
                <div className="flex justify-between border-t border-slate-600 pt-1 font-medium"><span className="text-slate-300">Total</span><span className="text-white">${orderTotal.toFixed(2)}</span></div>
              </div>
            )}

            {/* Buy button */}
            <button
              onClick={handlePurchase}
              disabled={purchasing || !userEmail || (isPhysical && listing.shipping_type === 'calculated' && !selectedShipping)}
              className="w-full py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-zenna-violet to-zenna-cyan hover:opacity-90 disabled:opacity-30 transition-all"
            >
              {purchasing ? 'Processing...' : orderTotal <= 0 ? '📥 Download Free' : `🛒 Buy Now — $${orderTotal.toFixed(2)}`}
            </button>

            {isPhysical && listing.shipping_type === 'calculated' && !selectedShipping && (
              <p className="text-[10px] text-amber-400 text-center mt-2">Enter your ZIP code above to see shipping options</p>
            )}

            {!userEmail && (
              <p className="text-[10px] text-slate-500 text-center mt-2">Sign in to purchase</p>
            )}

            {/* Share */}
            <div className="mt-4 pt-4 border-t border-slate-700">
              <ShareButtons
                imageUrl={listing.thumbnail_url}
                title={listing.title}
                pageUrl={`${window.location.origin}/marketplace/${listing.id}`}
                contentType="listing"
                contentId={listing.id}
                userEmail={userEmail}
                compact
                showWatermarkToggle={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN MARKETPLACE PAGE
// ============================================================================

export default function MarketplacePage({ userEmail = '' }) {
  const [listings, setListings] = useState([])
  const [featured, setFeatured] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  // Filters
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [craftType, setCraftType] = useState('')
  const [sortBy, setSortBy] = useState('newest')

  // Modal
  const [selectedListing, setSelectedListing] = useState(null)

  // Seller
  const [sellerStatus, setSellerStatus] = useState(null)
  const [sellerLoading, setSellerLoading] = useState(true)
  const [showCreateListing, setShowCreateListing] = useState(false)
  const [onboardingUrl, setOnboardingUrl] = useState(null)

  // Check seller status on mount
  useEffect(() => {
    if (!userEmail) { setSellerLoading(false); return }
    setSellerLoading(true)
    fetch(`${API_URL}/sellers/status`, { headers: { 'x-user-email': userEmail } })
      .then(r => r.json())
      .then(data => { setSellerStatus(data); setSellerLoading(false) })
      .catch(() => setSellerLoading(false))
  }, [userEmail])

  const loadListings = useCallback(async (pageNum = 1, append = false) => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: pageNum, limit: 24, sort: sortBy
      })
      if (search) params.append('search', search)
      if (category) params.append('category', category)
      if (craftType) params.append('craft_type', craftType)

      const res = await fetch(`${API_URL}/marketplace/listings?${params}`)
      const data = await res.json()

      if (append) {
        setListings(prev => [...prev, ...(data.listings || [])])
      } else {
        setListings(data.listings || [])
      }
      setHasMore((data.listings || []).length >= 24)
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }, [search, category, craftType, sortBy])

  const loadFeatured = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/marketplace/featured`)
      const data = await res.json()
      setFeatured(data.featured || [])
    } catch (err) { console.error(err) }
  }, [])

  useEffect(() => {
    setPage(1)
    loadListings(1)
  }, [loadListings])

  useEffect(() => { loadFeatured() }, [loadFeatured])

  const handleSearch = (e) => {
    e.preventDefault()
    setPage(1)
    loadListings(1)
  }

  const loadMore = () => {
    const next = page + 1
    setPage(next)
    loadListings(next, true)
  }

  const handleViewListing = async (listing) => {
    // Fetch full details
    try {
      const res = await fetch(`${API_URL}/marketplace/listings/${listing.id}`)
      const data = await res.json()
      setSelectedListing(data.listing)
    } catch { setSelectedListing(listing) }
  }

  const handleStartSelling = async () => {
    // If still loading status, fetch it now and wait
    if (sellerLoading || sellerStatus === null) {
      try {
        const res = await fetch(`${API_URL}/sellers/status`, { headers: { 'x-user-email': userEmail } })
        const data = await res.json()
        setSellerStatus(data)
        setSellerLoading(false)
        if (data.verified) {
          setShowCreateListing(true)
          return
        }
      } catch (err) {
        console.error('Status check error:', err)
        setSellerLoading(false)
      }
    }

    if (sellerStatus?.verified) {
      setShowCreateListing(true)
      return
    }

    // If already_verified comes back, just refresh status
    if (sellerStatus?.already_verified) {
      setShowCreateListing(true)
      return
    }

    // Start Stripe Connect onboarding
    try {
      const res = await fetch(`${API_URL}/sellers/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-email': userEmail }
      })
      const data = await res.json()
      if (data.already_verified) {
        setSellerStatus(prev => ({ ...prev, verified: true }))
        setShowCreateListing(true)
      } else if (data.url) {
        window.location.href = data.url
      }
    } catch (err) { console.error('Onboard error:', err) }
  }

  const CATEGORIES = [
    { value: '', label: 'All' },
    { value: 'designs', label: '🎨 Designs' },
    { value: 'templates', label: '📐 Templates' },
    { value: 'fonts', label: '🔤 Fonts' },
    { value: 'bundles', label: '📦 Bundles' },
    { value: 'physical', label: '🏠 Physical' },
    { value: 'kits', label: '🧵 Kits' },
    { value: 'supplies', label: '🛠️ Supplies' }
  ]

  return (
    <div className="min-h-screen bg-slate-900">
      <ZennaPageHeader title="Marketplace" />
      {/* Header */}
      <div className="bg-gradient-to-r from-zenna-violet/10 via-slate-900 to-zenna-cyan/10 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">Marketplace</h1>
              <p className="text-sm text-slate-400">Discover designs, templates, and fonts from the Zenna community</p>
            </div>
            {userEmail && (
              <button onClick={handleStartSelling} disabled={sellerLoading}
                className="flex-shrink-0 px-5 py-2.5 bg-gradient-to-r from-zenna-violet to-zenna-cyan text-white rounded-xl text-sm font-bold hover:shadow-lg hover:shadow-zenna-violet/20 transition-all disabled:opacity-60">
                {sellerLoading ? '...' : sellerStatus?.verified ? '✨ Create Listing' : '🚀 Start Selling'}
              </button>
            )}
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="mt-4 flex gap-2 max-w-xl">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search designs, templates, fonts..."
              className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-zenna-violet/50"
            />
            <button type="submit" className="px-5 py-2.5 bg-zenna-violet/80 hover:bg-zenna-violet text-white text-sm font-medium rounded-xl">
              Search
            </button>
          </form>
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3 overflow-x-auto no-scrollbar">
          {/* Categories */}
          {CATEGORIES.map(cat => (
            <button
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                category === cat.value
                  ? 'bg-zenna-violet/20 text-zenna-violet border border-zenna-violet/30'
                  : 'text-slate-400 bg-slate-800 hover:text-white border border-transparent'
              }`}
            >
              {cat.label}
            </button>
          ))}

          <div className="w-px h-6 bg-slate-700 flex-shrink-0" />

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-400 focus:outline-none flex-shrink-0"
          >
            <option value="newest">Newest</option>
            <option value="popular">Most Popular</option>
            <option value="rating">Top Rated</option>
            <option value="price_low">Price: Low → High</option>
            <option value="price_high">Price: High → Low</option>
          </select>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Featured section */}
        {featured.length > 0 && !search && !category && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-white mb-3">⭐ Featured</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {featured.slice(0, 6).map(listing => (
                <ListingCard key={listing.id} listing={listing} onView={handleViewListing} />
              ))}
            </div>
          </div>
        )}

        {/* All listings */}
        <h2 className="text-lg font-bold text-white mb-3">
          {search ? `Results for "${search}"` : category ? CATEGORIES.find(c => c.value === category)?.label || 'All' : 'All Designs'}
        </h2>

        {loading && listings.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-zenna-violet border-t-transparent rounded-full"></div>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-4xl mb-3">🛒</p>
            <h3 className="text-lg font-medium text-white mb-1">No listings yet</h3>
            <p className="text-sm text-slate-500">Be the first to sell your designs!</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {listings.map(listing => (
                <ListingCard key={listing.id} listing={listing} onView={handleViewListing} />
              ))}
            </div>

            {hasMore && (
              <div className="text-center mt-8">
                <button onClick={loadMore} disabled={loading}
                  className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-sm text-slate-300 rounded-xl border border-slate-700 disabled:opacity-50">
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Listing detail modal */}
      {selectedListing && (
        <ListingDetailModal
          listing={selectedListing}
          userEmail={userEmail}
          onClose={() => setSelectedListing(null)}
        />
      )}

      {/* Create listing modal */}
      <CreateListingModal
        isOpen={showCreateListing}
        onClose={() => setShowCreateListing(false)}
        userEmail={userEmail}
        onCreated={() => { loadListings(1); setShowCreateListing(false) }}
      />
    </div>
  )
}